import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/uploads";
import { WEIGHT_UNIT_LABEL, TEMP_UNIT_LABEL, DATE_FORMAT_LABEL } from "@/lib/format";
import { TwoFactorPanel, SessionsPanel, TrustedDevicesPanel } from "@/components/SecurityPanels";
import PreferencesForm from "@/components/settings/PreferencesForm";
import BillingTab from "@/components/settings/BillingTab";
import ExportsTab from "@/components/settings/ExportsTab";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";
import { User, Mail, KeyRound, Download, Trash2, AtSign, MapPin, Camera, Sliders, Bell, ShieldCheck, Monitor, Globe, CreditCard, Smartphone } from "lucide-react";

export default function Settings() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  // Scroll to billing section when arriving via the "Upgrade to Pro" pill
  useEffect(() => {
    if (sp.get("tab") === "billing") {
      const el = document.querySelector('[data-testid="section-billing"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [sp]);

  if (!user) return null;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Settings</h1>
      </div>

      <Section icon={User} title="Profile">
        <ProfileForm user={user} onSaved={refreshMe} />
      </Section>

      <Section icon={CreditCard} title="Billing">
        <BillingTab />
      </Section>

      <Section icon={Download} title="Exports & Reports">
        <ExportsTab />
      </Section>

      <Section icon={Sliders} title="Preferences">
        <PreferencesForm user={user} onSaved={refreshMe} />
      </Section>

      <Section icon={Bell} title="Notifications">
        <NotificationsForm user={user} onSaved={refreshMe} />
      </Section>

      <Section icon={Globe} title="Public profile">
        <PublicProfileSection user={user} onSaved={refreshMe} />
      </Section>

      <Section icon={Mail} title="Email">
        <EmailRow user={user} onSaved={refreshMe} />
      </Section>

      <Section icon={KeyRound} title="Password">
        <PasswordForm />
      </Section>

      <Section icon={ShieldCheck} title="Two-factor authentication">
        <TwoFactorPanel />
      </Section>

      <Section icon={Monitor} title="Active sessions">
        <SessionsPanel />
      </Section>

      <Section icon={Smartphone} title="Trusted devices">
        <TrustedDevicesPanel />
      </Section>

      <Section icon={Download} title="Your data">
        <ExportRow />
      </Section>

      <Section icon={Trash2} title="Danger zone" tone="danger">
        <DeleteRow onDeleted={() => { logout(); navigate("/"); toast.success("Account deleted"); }} />
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, tone, children }) {
  const ring = tone === "danger" ? "border-red-200" : "border-stone-200";
  const iconTone = tone === "danger" ? "text-red-600" : "text-stone-500";
  return (
    <section className={`bg-white border ${ring} rounded-2xl p-6`} data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${iconTone}`} />
        <h2 className="font-display text-xl font-medium">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ProfileForm({ user, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    bio: user.bio || "",
    location: user.location || "",
    avatar_url: user.avatar_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { secure_url } = await uploadToCloudinary(f);
      setForm((p) => ({ ...p, avatar_url: secure_url }));
      toast.success("Avatar uploaded — don't forget to save");
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      toast.success("Profile saved");
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-5">
        <div className="relative">
          {form.avatar_url ? (
            <img src={form.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover border border-stone-200" data-testid="avatar-preview" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-100 to-amber-50 grid place-items-center text-2xl font-display text-amber-700 border border-stone-200">
              {(form.name || user.email)?.[0]?.toUpperCase()}
            </div>
          )}
          <label className="absolute -bottom-1 -right-1 bg-white border border-stone-200 rounded-full p-1.5 cursor-pointer hover:bg-stone-50 shadow-sm" data-testid="avatar-upload-label">
            <Camera className="w-3.5 h-3.5 text-stone-700" />
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickAvatar} className="hidden" data-testid="avatar-file-input" />
          </label>
        </div>
        <div className="text-sm text-stone-600">
          <div className="font-medium text-stone-900">{user.email}</div>
          {user.created_at && <div className="text-xs text-stone-500">Member since {user.created_at.slice(0, 10)}</div>}
          {uploading && <div className="text-xs text-amber-700 mt-1">Uploading…</div>}
        </div>
      </div>

      <div>
        <Label>Display name</Label>
        <Input value={form.name} maxLength={200} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
      </div>
      <div>
        <Label className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Location</Label>
        <Input value={form.location} maxLength={200} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Linz, Austria" data-testid="profile-location-input" />
      </div>
      <div>
        <Label>Bio</Label>
        <Textarea rows={3} maxLength={2000} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Hobbyist beekeeper, 3 hives, urban garden..." data-testid="profile-bio-input" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="profile-save-button">
        {saving ? "Saving…" : "Save profile"}
      </Button>
    </div>
  );
}

function EmailRow({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-stone-800">
        <AtSign className="w-4 h-4 text-stone-400" />
        <span className="font-medium">{user.email}</span>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full border-stone-300" data-testid="change-email-button">Change email</Button>
        </DialogTrigger>
        <EmailChangeDialog onSaved={async () => { setOpen(false); await onSaved?.(); }} />
      </Dialog>
    </div>
  );
}

function EmailChangeDialog({ onSaved }) {
  const [password, setPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-email", { password, new_email: newEmail });
      toast.success("Email updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium">Change email</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Current password</Label>
          <Input type="password" value={password} maxLength={200} onChange={(e) => setPassword(e.target.value)} required data-testid="change-email-password-input" />
        </div>
        <div>
          <Label>New email</Label>
          <Input type="email" value={newEmail} maxLength={254} onChange={(e) => setNewEmail(e.target.value)} required data-testid="change-email-new-input" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="change-email-submit">
            {saving ? "Saving…" : "Update email"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function PasswordForm() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return toast.error("New passwords don't match");
    if (!evaluatePassword(form.next).meetsPolicy) {
      return toast.error("Password doesn't meet the minimum requirements");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      if (data.token) localStorage.setItem("beekeep_token", data.token);
      toast.success("Password changed");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label>Current password</Label>
        <Input type="password" value={form.current} maxLength={200} onChange={(e) => setForm({ ...form, current: e.target.value })} required data-testid="password-current-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>New password</Label>
          <Input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required minLength={8} maxLength={200} data-testid="password-new-input" />
        </div>
        <div>
          <Label>Confirm</Label>
          <Input type="password" value={form.confirm} maxLength={200} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="password-confirm-input" />
        </div>
      </div>
      <PasswordStrength password={form.next} testidPrefix="change-password-strength" />
      <Button type="submit" disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="password-save-button">
        {saving ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}

function ExportRow() {
  const [busy, setBusy] = useState(false);
  const exportData = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("beekeep_token");
      const res = await fetch(`${API}/auth/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : "hive-buzzzillion-export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm text-stone-600 max-w-md">
        Download a ZIP of everything you've logged: profile, hives, journal entries, reminders, harvests, and AI diagnoses.
      </p>
      <Button variant="outline" className="rounded-full border-stone-300" onClick={exportData} disabled={busy} data-testid="export-data-button">
        <Download className="w-4 h-4 mr-2" /> {busy ? "Preparing…" : "Export my data"}
      </Button>
    </div>
  );
}

function DeleteRow({ onDeleted }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm text-stone-600 max-w-md">
        Permanently delete your account and all hives, journals, reminders, harvests, and diagnoses.
        This cannot be undone.
      </p>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setConfirmText(""); } }}>
        <DialogTrigger asChild>
          <Button variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="delete-account-button">
            <Trash2 className="w-4 h-4 mr-2" /> Delete account
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-red-700">Delete your account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-700">
              This will permanently erase all your data. There is no undo.
              Type <strong>DELETE</strong> and enter your password to confirm.
            </p>
            <div>
              <Label>Type DELETE</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-text" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-confirm-password" />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="destructive"
              className="rounded-full"
              disabled={busy || confirmText !== "DELETE" || !password}
              onClick={submit}
              data-testid="delete-confirm-button"
            >
              {busy ? "Deleting…" : "Yes, delete my account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NotificationsForm({ user, onSaved }) {
  const [form, setForm] = useState({
    notify_in_app: user.notify_in_app ?? true,
    notify_email: user.notify_email ?? false,
  });
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", next);
      setForm(next);
      toast.success("Notifications updated");
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ul className="space-y-3">
      <li className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-stone-900">In-app reminders</div>
          <div className="text-xs text-stone-500">Show upcoming inspection &amp; treatment reminders on the dashboard.</div>
        </div>
        <Switch
          checked={form.notify_in_app}
          disabled={saving}
          onCheckedChange={(v) => save({ ...form, notify_in_app: v })}
          data-testid="notify-in-app-switch"
        />
      </li>
      <li className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-stone-900">Monthly season summary email</div>
          <div className="text-xs text-stone-500">Auto-sent at month start when activity is present (powered by Brevo).</div>
        </div>
        <Switch
          checked={form.notify_email}
          disabled={saving}
          onCheckedChange={(v) => save({ ...form, notify_email: v })}
          data-testid="notify-email-switch"
        />
      </li>
      <li className="pt-2">
        <SummaryNowButton />
      </li>
    </ul>
  );
}

function SummaryNowButton() {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/engagement/summary/email");
      toast.success("Season summary queued — check your inbox in a few seconds.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={send} disabled={busy} className="rounded-full border-stone-300" data-testid="summary-send-now-button">
      {busy ? "Sending…" : "Email me my summary now"}
    </Button>
  );
}

function PublicProfileSection({ user, onSaved }) {
  const [enabled, setEnabled] = useState(Boolean(user.feature_publicly));
  const [saving, setSaving] = useState(false);

  const toggle = async (v) => {
    setSaving(true);
    setEnabled(v);
    try {
      await api.patch("/auth/profile", { feature_publicly: v });
      await onSaved?.();
      toast.success(v ? "Your apiary may now be featured" : "Removed from featured pool");
    } catch (e) {
      setEnabled(!v);
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="public-profile-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-stone-900">Feature my apiary on the landing page</div>
          <div className="text-xs text-stone-500 mt-1 max-w-md">
            Show up in the <strong>Hive of the Week</strong> rotation. Requires at least one active, password-free apiary share link from the <em>Hives</em> page. We surface your name, location, hive count and badges — nothing private.
          </div>
        </div>
        <Switch checked={enabled} disabled={saving} onCheckedChange={toggle} data-testid="feature-publicly-switch" />
      </div>
    </div>
  );
}
